import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Plus, Loader2, FileText, Shield } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { usePatientInvoices } from '@/hooks/usePatientInvoices';
import { formatMoney } from '@/hooks/useInvoices';
import { InvoiceStatusBadges } from '@/pages/invoices/Invoices';

interface InvoiceTabProps {
  patientId: string;
  clinicId: string;
}

export function InvoiceTab({ patientId, clinicId }: InvoiceTabProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: invoices, isLoading } = usePatientInvoices(patientId);

  const handleCreateInvoice = () => {
    const params = new URLSearchParams({
      patient_id: patientId,
      clinic_id: clinicId,
      return_to: `/patients/${patientId}`,
    });
    navigate(`/invoices/new?${params.toString()}`);
  };

  const handleRowClick = (invoiceId: string) => {
    navigate(`/invoices/${invoiceId}`);
  };

  const formatDate = (dateString: string | null): string => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('da-DK', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  // Check if invoice is overdue (issued but not paid and due date has passed)
  const isOverdue = (invoice: NonNullable<typeof invoices>[number]): boolean => {
    if (invoice.status === 'paid' || invoice.status === 'void' || !invoice.due_at) {
      return false;
    }
    return new Date(invoice.due_at) < new Date();
  };

  return (
    <section>
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-base font-semibold text-gray-900 dark:text-white">
          {t('invoices.section_title', 'Invoices')}
        </h2>
        <Button onClick={handleCreateInvoice} size="sm">
          <Plus className="h-4 w-4" />
          {t('invoices.new_invoice', 'New Invoice')}
        </Button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center rounded-lg border border-gray-200 bg-white p-12 dark:border-gray-700 dark:bg-card-dark">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      ) : invoices && invoices.length === 0 ? (
        <div className="rounded-lg border border-gray-200 bg-white p-10 text-center dark:border-gray-700 dark:bg-card-dark">
          <FileText className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">
            {t('invoices.no_invoices_title', 'No invoices yet')}
          </h3>
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            {t('invoices.no_invoices_description', 'Create your first invoice for this patient')}
          </p>
          <Button onClick={handleCreateInvoice} className="mt-6">
            <Plus className="mr-2 h-4 w-4" />
            {t('invoices.create_first_invoice', 'Create Invoice')}
          </Button>
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-card-dark">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400"
                  >
                    {t('invoices.col_invoice_number', 'Invoice #')}
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400"
                  >
                    {t('invoices.col_date', 'Date')}
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400"
                  >
                    {t('invoices.col_due_date', 'Due Date')}
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400"
                  >
                    {t('invoices.col_amount', 'Amount')}
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400"
                  >
                    {t('invoices.col_status', 'Status')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-card-dark">
                {invoices?.map((invoice) => (
                  <tr
                    key={invoice.id}
                    onClick={() => handleRowClick(invoice.id)}
                    className="cursor-pointer transition-colors hover:bg-gray-50 dark:hover:bg-gray-700/50"
                  >
                    <td className="whitespace-nowrap px-6 py-4">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-900 dark:text-white">
                          {invoice.invoice_number || (
                            <span className="italic text-gray-400">
                              {t('invoices.status_draft', 'Draft')}
                            </span>
                          )}
                        </span>
                        {(invoice as any).sd_submitted_at && (
                          <Shield
                            className="h-4 w-4 text-green-600 dark:text-green-400"
                          />
                        )}
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                      {formatDate(invoice.issued_at || invoice.created_at)}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                      {isOverdue(invoice) ? (
                        <span className="font-medium text-red-600 dark:text-red-400">
                          {formatDate(invoice.due_at)}
                        </span>
                      ) : (
                        formatDate(invoice.due_at)
                      )}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm font-medium text-gray-900 dark:text-white">
                      {formatMoney(invoice.total_cents, invoice.currency)}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4">
                      <InvoiceStatusBadges
                        status={invoice.status}
                        lastSentAt={invoice.last_sent_at}
                      />
                      {isOverdue(invoice) && invoice.status === 'issued' && (
                        <span className="ml-2 inline-flex rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-800 dark:bg-red-900/30 dark:text-red-300">
                          · {t('invoices.overdue', 'Overdue')}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </section>
  );
}
